import { getDateStrSAST } from '@/lib/sastTime';
import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';

export const APPOINTMENT_MISSED_CUTOFF_HOUR_SAST = 7;

type AppointmentMissedCandidate = {
	id: string;
	status?: string | null;
	scheduled_start?: string | Date | null;
	custom_fields?: Record<string, any> | string | null;
};

function normalizeAppointmentCustomFields(customFields: AppointmentMissedCandidate['custom_fields']): Record<string, any> {
	if (!customFields) {
		return {};
	}

	if (typeof customFields === 'string') {
		try {
			const parsed = JSON.parse(customFields);
			return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
		} catch {
			return {};
		}
	}

	return typeof customFields === 'object' && !Array.isArray(customFields) ? customFields : {};
}

export function hasAppointmentEverLeftScheduledState(customFields: AppointmentMissedCandidate['custom_fields']): boolean {
	const normalizedCustomFields = normalizeAppointmentCustomFields(customFields);

	return Boolean(
		normalizedCustomFields.travel_started_at
		|| normalizedCustomFields.arrived_at
		|| normalizedCustomFields.check_in_timestamp
		|| normalizedCustomFields.check_out_timestamp,
	);
}

export function getAppointmentMissedCutoffDateSAST(scheduledStart?: string | Date | null): Date | null {
	if (!scheduledStart) {
		return null;
	}

	const parsedScheduledStart = scheduledStart instanceof Date ? scheduledStart : new Date(scheduledStart);
	if (Number.isNaN(parsedScheduledStart.getTime())) {
		return null;
	}

	const scheduledDateStrSast = getDateStrSAST(parsedScheduledStart);
	const [year, month, day] = scheduledDateStrSast.split('-').map((value) => Number.parseInt(value, 10));

	if (![year, month, day].every(Number.isFinite)) {
		return null;
	}

	return new Date(Date.UTC(year, month - 1, day + 1, APPOINTMENT_MISSED_CUTOFF_HOUR_SAST - 2, 0, 0, 0));
}

export function shouldAutoMarkAppointmentMissed(
	appointment: AppointmentMissedCandidate,
	now: Date = new Date(),
): boolean {
	if (normalizeWorkflowStatusKey(appointment.status) !== 'scheduled') {
		return false;
	}

	if (hasAppointmentEverLeftScheduledState(appointment.custom_fields)) {
		return false;
	}

	const cutoffDate = getAppointmentMissedCutoffDateSAST(appointment.scheduled_start);
	if (!cutoffDate) {
		return false;
	}

	return now.getTime() >= cutoffDate.getTime();
}

export async function syncMissedAppointmentsForClient(
	executor: any,
	clientId: number,
	options: { appointmentIds?: string[]; now?: Date } = {},
): Promise<string[]> {
	const appointmentIds = Array.isArray(options.appointmentIds)
		? Array.from(new Set(options.appointmentIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)))
		: [];
	const now = options.now ?? new Date();

	const candidateQuery = executor('work_items')
		.where({ client_id: clientId, item_type: 'appointment', status: 'scheduled' })
		.whereNull('deleted_at')
		.whereNotNull('scheduled_start')
		.select('id', 'status', 'scheduled_start', 'custom_fields');

	if (appointmentIds.length > 0) {
		candidateQuery.whereIn('id', appointmentIds);
	}

	const candidates = await candidateQuery;
	const appointmentsToMarkMissed = candidates
		.filter((appointment: AppointmentMissedCandidate) => shouldAutoMarkAppointmentMissed(appointment, now))
		.map((appointment: AppointmentMissedCandidate) => appointment.id);

	if (appointmentsToMarkMissed.length === 0) {
		return [];
	}

	await executor('work_items')
		.where({ client_id: clientId, item_type: 'appointment', status: 'scheduled' })
		.whereIn('id', appointmentsToMarkMissed)
		.whereNull('deleted_at')
		.update({
			status: 'missed',
			updated_at: now,
		});

	return appointmentsToMarkMissed;
}

export function workItemTypeFilterIncludesAppointments(itemTypeFilter?: string | string[] | null): boolean {
	if (!itemTypeFilter) {
		return true;
	}

	const itemTypes = Array.isArray(itemTypeFilter) ? itemTypeFilter : [itemTypeFilter];
	return itemTypes.some((itemType) => normalizeWorkflowStatusKey(itemType) === 'appointment');
}